// Schema defination
import { type Static, Type } from '@sinclair/typebox';
import { z } from 'zod';
import { toTypedSchema } from '@vee-validate/zod';
import {
	validateNameFields,
	validatePhoneNumber,
	validateZipCode,
	validateEmail,
	validateEmoji,
} from '~/utils/checkout';
import { fieldExists } from '~/utils/checkout';

export const ShippingAddress = z.object({
	firstName: z
		.string({ invalid_type_error: 'Please add your first name.' })
		.min(1, { message: 'Please add your first name.' })
		.refine((data) => data.trim().length > 0, {
			message: 'First name cannot be empty spaces.',
		})
		.refine((data: any) => validateNameFields(data), {
			message: 'Please enter valid first name.',
		}),
	lastName: z
		.string({ invalid_type_error: 'Please add your last name.' })
		.min(1, { message: 'Please add your last name.' })
		.refine((data) => data.trim().length > 0, {
			message: 'Last name cannot be empty spaces.',
		})
		.refine((data: any) => validateNameFields(data), {
			message: 'Please enter valid last name.',
		}),
	address1: z
		.string({ invalid_type_error: 'Please add your address.' })
		.min(1, { message: 'Please add your address.' })
		.refine((data) => data.trim().length > 0, {
			message: 'Address cannot be empty spaces.',
		})
		.refine(
			(data) => {
				// remove trailing and leading junk from string and check if its email
				const cleaned = data
					.trim()
					.replace(/^[^a-zA-Z0-9@]+|[^a-zA-Z0-9@]+$/g, '');

				return !validateEmail(cleaned);
			},
			{
				message: 'Please add your address.',
			},
		)
		.refine((data) => !validateEmoji(data), {
			message: 'Address cannot contain emojis.',
		})
		.refine((data) => !/'/.test(data), {
			message: 'Address cannot contain apostrophes.',
		})
		.refine(
			(data) => {
				if (!data) {
					return true;
				}
				// eslint-disable-next-line no-control-regex
				const regex = /^[\u0000-\u007F]+$/;
				return regex.test(data);
			},
			{
				message: 'Address must contain only English characters.',
			},
		),

	address2: z
		.string()
		.refine((data) => !validateEmoji(data), {
			message: 'Address cannot contain emojis.',
		})
		.refine((data) => !/'/.test(data), {
			message: 'Address cannot contain apostrophes.',
		})
		.refine(
			(data) => {
				if (!data) {
					return true;
				}
				// eslint-disable-next-line no-control-regex
				const regex = /^[\u0000-\u007F]+$/;
				return regex.test(data);
			},
			{
				message: 'Address must contain only English characters.',
			},
		),
	city: z
		.string()
		.optional()
		.refine((data) => !/'/.test(data!), {
			message: 'City cannot contain apostrophes.',
		}),
	zip: z.string().optional(),
	province: z.string().nullable().optional(),
});

export const BillingAddress = ShippingAddress.extend({
	countryCode: z.string(),
	phoneNumber: z.string().min(1, { message: 'Phone number is required.' }),
	phonePlaceholderPattern: z.string().optional(),
	email: z
		.string({ invalid_type_error: 'Please enter valid email address.' })
		.email({
			message: 'Please enter valid email address.',
		}),
})
	.refine(
		(data: any) => validatePhoneNumber(data.countryCode, data.phoneNumber),
		{
			message: 'Please enter valid phone number.',
			path: ['phoneNumber'],
		},
	)
	.refine(
		(data) => {
			const haveProvince = fieldExists(data.countryCode, 'province');
			if (haveProvince) {
				if (!data.province) {
					return false;
				}
				return data.province.length > 0;
			}
			return true;
		},
		{
			message: 'Please select province/state.',
			path: ['province'],
		},
	);

export const ContactInformation = z.object({
	email: z
		.string({ invalid_type_error: 'Please enter valid email address.' })
		.email({
			message: 'Please enter valid email address.',
		}),
	// phone number data
	phoneNumber: z
		.string({ invalid_type_error: 'Please enter valid phone number.' })
		.min(1, { message: 'Please enter valid phone number.' }),
	phonePlaceholderPattern: z.string().optional(),

	// phone number data ends
});

export const EidGift = z
	.object({
		from: z
			.string({ required_error: 'Enter sender’s first and last name' })
			.refine((data) => data.length > 0, {
				message: 'Enter sender’s first and last name',
			}),
		to: z
			.string({ required_error: 'Enter recipient’s first and last name' })
			.min(1, { message: 'Enter recipient’s first and last name' }),
		messageType: z
			.string({ required_error: 'Select a message type' })
			.min(1, { message: 'Select a message type' }),
		message: z.string().optional(),
	})
	.refine(
		(data) => {
			if (data.messageType === 'preMade') {
				return data.message?.length! > 0;
			}
			return true;
		},
		{
			message: 'Select a message',
			path: ['message'],
		},
	)
	.refine(
		(data) => {
			if (data.messageType === 'custom') {
				return data.message?.length! > 0;
			}
			return true;
		},
		{
			message: 'Enter a message',
			path: ['message'],
		},
	);
export const CheckoutSchemaDefinitionNew = toTypedSchema(
	z
		.object({
			countryCode: z.string().min(1, {
				message: 'Please select country.',
			}),
			contactInformation: ContactInformation,
			shippingAddress: ShippingAddress,
			creditCardDiscount: z.number().optional(),
			eidGift: EidGift.optional(),
			isOOSItems: z.boolean().refine(
				(val) => {
					if (val) return false;

					return true;
				},
				{
					message: 'Please remove out of stock items to place order',
				},
			),
			paymentMethod: z
				.string()
				.min(1, {
					message: 'Please select a payment method.',
				})
				.refine(
					(name: string) => {
						if (name === PAYMENT_METHODS.BANK_DEPOSIT_DISABLED) return false;

						return true;
					},
					{
						message: 'Please select available payment method.',
					},
				),
			billingAddress: BillingAddress.optional(),

			billingAddressType: z.string(),
			checkoutLoading: z.boolean().nullable().optional(),
			usingRewards: z.boolean(),
			xpayFormReady: z.boolean().refine((val) => val === true, {
				message: 'Please enter card details',
			}),
			stripeFormReady: z.boolean().refine((val) => val === true, {
				message: 'Please enter card details',
			}),
			couponCode: z.string().optional(),
			couponType: z.string().optional(),
			couponDiscountType: z.string().optional(),
			deliveryMethod: z.string().refine(
				(data) => {
					return !!data;
				},
				{
					message: 'Please select a delivery method.',
				},
			),
		})
		.refine(
			(data: any) =>
				validatePhoneNumber(
					data.countryCode,
					data.contactInformation.phoneNumber,
				),
			{
				message: 'Please enter valid phone number.',
				path: ['contactInformation.phoneNumber'],
			},
		)
		.refine(
			(data) => {
				// get fields for country
				// check if its has zip
				// if not empty zip is valid, let it go
				if (data.countryCode === 'PK') {
					return true;
				}
				const haveZip = fieldExists(data.countryCode, 'zip');
				if (haveZip) {
					return validateZipCode(data.shippingAddress.zip!, data.countryCode);
				}
				return true;
			},
			{
				message: 'Please enter valid zip code.',
				path: ['shippingAddress.zip'],
			},
		)
		.refine(
			(data) => {
				const haveProvince = fieldExists(data.countryCode, 'province');
				if (haveProvince) {
					if (!data.shippingAddress.province) {
						return false;
					}
					return data?.shippingAddress?.province?.length > 0;
				}
				return true;
			},
			{
				message: 'Please select province/state.',
				path: ['shippingAddress.province'],
			},
		)
		.refine(
			(data) => {
				const haveCity = fieldExists(data.countryCode, 'city');
				if (
					haveCity &&
					(data?.shippingAddress?.city?.length! === 0 ||
						data?.shippingAddress?.city?.trim().length! === 0)
				) {
					return false;
				}
				return true;
			},
			{
				message: 'Please add city.',
				path: ['shippingAddress.city'],
			},
		)
		.refine(
			(data) => {
				const haveCity = fieldExists(data.countryCode, 'city');
				if (!haveCity) {
					return true;
				}
				// eslint-disable-next-line no-control-regex
				const regex = /^[\u0000-\u007F]+$/;
				return regex.test(data?.shippingAddress?.city!);
			},
			{
				message: 'City must contain only English characters.',
				path: ['shippingAddress.city'],
			},
		),
);

export type ShippingAddressType = z.infer<typeof ShippingAddress>;
export type ContactInformationType = z.infer<typeof ContactInformation>;
export type BillingAddressType = z.infer<typeof BillingAddress>;

interface PlaceOrderPayLoad {
	checkoutHash: string;
	currencyCode?: string;
	noteAttributes?: { name: string; value: string }[];
	checkoutData: {
		loganId: string | undefined;
		shippingAddress: ShippingAddressType;
		billingAddress: {
			phoneNumber: string;
			address1?: string | undefined;
			address2?: string | undefined;
			city?: string | undefined;
			firstName?: string | undefined;
			lastName?: string | undefined;
			countryCode?: string | undefined;
			zip?: string | undefined;
			province?: string | undefined;
			phonePlaceholderPattern?: string | undefined;
		};
		gaClientId: string;
		lineItems: {
			variantId: string;
			quantity: number;
			customAttributes: {
				key: string;
				value: string;
			}[];
		}[];
		isApp: boolean;
		phonePrefix: number;
		shippingCountryCode: string;
		isFreeShipping: boolean;
		userUId: string | null | undefined;
		domain: string;
		email: string;
		paymentMethod: string;
		creditCardDiscount: string;

		rewardsEnabled: boolean;
		rewardsAmount: string;
		utm: string | null;
		xpayOrderReference?: string;
		redisBypass: boolean;
		couponCode: string | undefined;
	};
	loganId: string | undefined;
}
export type { PlaceOrderPayLoad };

//Countries interface
const CountrySchema = Type.Object({
	name: Type.String(),
	native: Type.String(),
	phone: Type.Array(Type.Number()),
	continent: Type.String(),
	capital: Type.String(),
	currency: Type.Array(Type.String()),
	languages: Type.Array(Type.String()),
	flag: Type.String(),
	pattern: Type.String(),
	placeholderPattern: Type.String(),
	provinceKey: Type.String(),
	labels: Type.Record(Type.String(), Type.String()),
	zones: Type.Optional(
		Type.Array(
			Type.Object({
				name: Type.String(),
				code: Type.String(),
			}),
		),
	),
	orderedFields: Type.Array(Type.Array(Type.String())),
});

export type Country = Static<typeof CountrySchema>;
